import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import Testimonials from '../components/Testimonials';
import CTA from '../components/Cta';
import HowItWorks from '../components/HowItWorks';
import NFCCards from '../components/NFCCards';
import { useSEO } from '../hooks/useSEO';

const BusinessCard = () => {
  const [activeFeature, setActiveFeature] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  
  useSEO({
    title: "Business Digital Cards for Professionals | AM CARDMATE",
    description: "Professional digital business cards for modern businesses. Enhanced features, analytics, and team management capabilities.",
    image: "/assets/businesscard.jpg",
    keywords: "business cards, professional cards, company cards, business networking, team cards"
  });

  const subscriptionPlans = [
    {
      title: 'Monthly',
      price: '£4.99',
      period: '/month',
      features: [
        'Create up to 20 professional cards',
        'Comprehensive analytics dashboard',
        'Unlimited card shares & updates',
        'Premium backgrounds & themes',
        'Priority customer support',
        'Cancel anytime'
      ],
      popular: false,
      buttonText: 'Start Monthly',
      gradient: 'from-teal-400 to-teal-600',
      description: 'Perfect for professionals getting started'
    },
    {
      title: 'Annual',
      price: '£39.99',
      period: '/year',
      yearlyPrice: '£3.33/month',
      features: [
        'Everything in Monthly plan',
        'Save 33% compared to monthly',
        'Advanced analytics exports',
        'Verified business badge',
        'Priority feature access',
        'Same-day support response'
      ],
      popular: true,
      buttonText: 'Save with Annual',
      gradient: 'from-blue-400 to-purple-600',
      description: 'Best value for growing businesses',
      savings: '4 months FREE'
    },
    {
      title: 'Lifetime',
      price: '£99.99',
      period: 'one-time',
      features: [
        'Everything in Annual plan',
        'FREE Premium NFC Card included',
        'Lifetime access to all features',
        'All future updates included',
        'VIP support channel',
        'No recurring fees ever'
      ],
      popular: false,
      buttonText: 'Go Lifetime',
      gradient: 'from-purple-400 to-pink-600',
      description: 'One payment, unlimited potential',
      bonus: 'Includes NFC Card'
    }
  ];

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Business Digital Cards",
        "provider": {
          "@type": "Organization",
          "name": "AM CARDMATE"
        },
        "description": "Professional digital business cards for businesses",
        "areaServed": "Worldwide",
        "offers": subscriptionPlans.map(plan => ({
          "@type": "Offer",
          "name": plan.title,
          "price": plan.price.replace('£', ''),
          "priceCurrency": "GBP",
          "description": plan.description
        }))
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const businessCardFeatures = [
    {
      icon: 'layer-group',
      title: 'Up to 20 Cards',
      description: 'Create multiple cards for different roles, events, or purposes - all managed from one account.',
      gradient: 'from-blue-400 to-blue-600'
    },
    {
      icon: 'chart-line',
      title: 'Advanced Analytics',
      description: 'Track every interaction with comprehensive insights: views, shares, QR scans, and contact saves.',
      gradient: 'from-purple-400 to-purple-600'
    },
    {
      icon: 'palette',
      title: 'Premium Customization',
      description: 'Professional templates and custom backgrounds to match your brand identity perfectly.',
      gradient: 'from-pink-400 to-pink-600'
    },
    {
      icon: 'shield-alt',
      title: 'Enterprise Security',
      description: 'Bank-level encryption and privacy controls to protect your business information.',
      gradient: 'from-green-400 to-green-600'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 lg:pt-32 pb-32 bg-gradient-to-b from-white via-gray-50/50 to-white relative overflow-hidden">
        <div className="absolute top-10 right-10 w-[700px] h-[700px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="building" />
              <span>Business Solutions</span>
            </div>
            
            <h1 className="text-h1 mb-4 md:mb-6">
              Professional <span className="text-gradient-blue-pink">Digital Business Cards</span> That Scale
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-8">
              Create up to 20 professional cards with comprehensive analytics, premium customization, and enterprise-grade features. Everything your business needs in one powerful platform.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto mb-10">
              <div className="text-center group cursor-pointer">
                <div className="text-h3 text-gradient-blue-pink mb-1">20</div>
                <div className="text-body-sm text-gray group-hover:text-primary-blue transition-colors">Cards per Account</div>
              </div>
              <div className="text-center group cursor-pointer">
                <div className="text-h3 text-gradient-blue-pink mb-1">Advanced</div>
                <div className="text-body-sm text-gray group-hover:text-primary-blue transition-colors">Analytics</div>
              </div>
              <div className="text-center group cursor-pointer">
                <div className="text-h3 text-gradient-blue-pink mb-1">Premium</div>
                <div className="text-body-sm text-gray group-hover:text-primary-blue transition-colors">Features</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => setShowComingSoonPopup(true)}
                className="btn btn-primary group w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="download" />
                <span>Download the App</span>
                <FontAwesomeIcon 
                  icon="arrow-right" 
                  className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                />
              </button>
              
              <a 
                href="#subscription-plans" 
                className="btn btn-outline group w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="tag" />
                <span>View Pricing</span>
              </a>
            </div>
          </div>
          
          {/* Dashboard Preview */}
          <div className="relative max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-xl bg-white">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-body text-white font-bold">Professional Dashboard</div>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
              </div>
              
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <div>
                    <h3 className="text-h5 text-dark mb-6 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white">
                        <FontAwesomeIcon icon="id-card" />
                      </div>
                      Your Cards (5/20)
                    </h3>
                    
                    <div className="space-y-3">
                      {[
                        { name: 'CEO Card', type: 'Executive', status: 'active' },
                        { name: 'Sales Team Card', type: 'Department', status: 'active' },
                        { name: 'Conference 2024', type: 'Event', status: 'inactive' }
                      ].map((card, i) => (
                        <div 
                          key={i} 
                          className="bg-gray-50 rounded-xl p-4 flex items-center justify-between hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                              <FontAwesomeIcon icon="id-card" />
                            </div>
                            <div>
                              <div className="text-body font-semibold text-dark">{card.name}</div>
                              <div className="text-body-sm text-gray">{card.type}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${card.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <button className="text-primary-blue hover:bg-blue-100 p-2 rounded-lg transition-colors">
                              <FontAwesomeIcon icon="share" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-h5 text-dark mb-6 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center text-white">
                        <FontAwesomeIcon icon="chart-line" />
                      </div>
                      Live Analytics
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-body text-gray">Total Views This Month</span>
                          <span className="text-h4 text-primary-blue">2,847</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full w-3/4"></div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-body text-gray">Contact Saves</span>
                          <span className="text-h4 text-primary-pink">486</span>
                        </div>
                        <div className="text-body-sm text-green-600 flex items-center gap-1">
                          <FontAwesomeIcon icon="arrow-up" />
                          <span>32% increase from last month</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Digital Section */}
      <section className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-white via-blue-50/20 to-white">
        <div className="absolute top-[20%] left-[10%] w-[500px] h-[500px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Image */}
              <div className="relative">
                <div className="relative max-w-full lg:max-w-xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-blue-100/50 rounded-3xl transform -rotate-3 scale-105"></div>
                  
                  <img 
                    src="/assets/image20.png"
                    alt="Business networking success" 
                    className="relative z-10 w-full rounded-3xl shadow-2xl"
                    style={{ 
                      minHeight: '400px',
                      maxHeight: '600px',
                      objectFit: 'cover' 
                    }}
                  />
                  
                  <div className="absolute -top-2 -left-4 lg:-top-14 lg:-left-6 bg-white rounded-2xl p-4 lg:p-5 shadow-xl z-20">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white">
                        <FontAwesomeIcon icon="users" className="text-lg lg:text-xl" />
                      </div>
                      <div>
                        <p className="text-body font-bold">Team Efficiency</p>
                        <p className="text-h4 text-blue-600">+67%</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-4 -right-4 lg:-bottom-10 lg:-right-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl p-4 lg:p-5 shadow-xl z-20">
                    <p className="text-h4">5x</p>
                    <p className="text-body-sm">Lead Generation</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div>
                <div className="badge badge-blue inline-flex mb-6">
                  <FontAwesomeIcon icon="trophy" />
                  <span>Business Impact</span>
                </div>
                
                <h2 className="text-h2 mb-6">
                  Why Businesses Choose <span className="text-gradient-blue-pink">Digital Excellence</span>
                </h2>
                
                <p className="text-body-lg text-gray-600 mb-8 leading-relaxed">
                  Forward-thinking companies are transforming their professional networking with AM CARDMATE. From startups to enterprises, see how digital business cards drive real business results.
                </p>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-6 mb-10">
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="text-h3 text-gradient-blue-pink mb-2">78%</div>
                    <p className="text-body text-gray-600">Faster follow-ups</p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="text-h3 text-gradient-blue-pink mb-2">92%</div>
                    <p className="text-body text-gray-600">Contact accuracy</p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="text-h3 text-gradient-blue-pink mb-2">5x</div>
                    <p className="text-body text-gray-600">More referrals</p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="text-h3 text-gradient-blue-pink mb-2">100%</div>
                    <p className="text-body text-gray-600">Brand consistency</p>
                  </div>
                </div>
                
                <button onClick={() => setShowComingSoonPopup(true)} className="btn btn-primary group inline-flex">
                  <FontAwesomeIcon icon="rocket" />
                  <span>Transform Your Business</span>
                  <FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <div className="bg-gradient-to-b from-white to-blue-50/30">
        <HowItWorks />
      </div>
      
      {/* Analytics Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/30" id="analytics-dashboard">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <div className="badge badge-purple inline-flex mb-4">
              <FontAwesomeIcon icon="chart-line" />
              <span>Analytics</span>
            </div>
            
            <h2 className="text-h2 mb-4 md:mb-6">
              Track Every <span className="text-gradient-blue-pink">Interaction</span>
            </h2>
            
            <p className="text-body-lg text-gray mb-6 md:mb-8">
              Get actionable insights with our comprehensive analytics dashboard. Know exactly how your cards perform.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'eye', title: 'Profile Views', desc: 'Track every view with device type and interaction data', color: 'from-blue-400 to-blue-600' },
              { icon: 'share-alt', title: 'Share Analytics', desc: 'Monitor how your cards spread across networks', color: 'from-purple-400 to-purple-600' },
              { icon: 'qrcode', title: 'QR & NFC Scans', desc: 'Track physical interactions and tap metrics', color: 'from-pink-400 to-pink-600' },
              { icon: 'save', title: 'Contact Saves', desc: 'Know when contacts save your information', color: 'from-green-400 to-green-600' }
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mb-4 shadow-md`}>
                  <FontAwesomeIcon icon={item.icon} className="text-2xl" />
                </div>
                <h3 className="text-card-title text-dark mb-2">{item.title}</h3>
                <p className="text-card-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 mb-8">
              <span className="text-white">Ready to </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Transform Your Business?</span>
            </h2>
            <button 
              onClick={() => setShowComingSoonPopup(true)}
              className="btn inline-flex items-center gap-3 px-8 py-4 text-btn-lg bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg"
            >
              <FontAwesomeIcon icon="download" />
              <span>Download the App</span>
            </button>
          </div>
        </div>
      </section>

      {/* Premium Features Section */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-b from-purple-50/30 via-white to-white">
        <div className="absolute bottom-[10%] right-[10%] w-[600px] h-[600px] bg-gradient-to-br from-purple-200/20 via-pink-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="badge badge-purple inline-flex mb-6">
                <FontAwesomeIcon icon="crown" />
                <span>Premium Features</span>
              </div>
              
              <h2 className="text-h2 mb-6">
                Everything You Need to{' '}
                <span className="text-gradient-blue-pink">Stand Out</span>
              </h2>
              
              <p className="text-body-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Unlock the full potential of digital networking with our premium suite of features designed for professionals who mean business.
              </p>
            </div>
            
            {/* Features Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Features */}
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {businessCardFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <div 
                        className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-md mb-4`}
                      >
                        <FontAwesomeIcon icon={feature.icon} className="text-white text-xl" />
                      </div>
                      
                      <h3 className="text-card-title text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-card-body">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4 mt-10">
                  <button onClick={() => setShowComingSoonPopup(true)} className="btn btn-primary shadow-lg hover:shadow-xl">
                    <FontAwesomeIcon icon="download" />
                    <span>Download App</span>
                  </button>
                  
                  <a href="#subscription-plans" className="btn btn-outline">
                    <span>View Pricing</span>
                  </a>
                </div>
              </div>
              
              {/* Image */}
              <div className="order-1 lg:order-2">
                <div className="relative max-w-full lg:max-w-xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-3xl transform rotate-3 scale-105"></div>
                  
                  <img 
                    src="/assets/image24.png" 
                    alt="Premium Features Dashboard" 
                    className="relative z-10 w-full rounded-3xl shadow-2xl"
                    style={{
                      minHeight: '400px',
                      maxHeight: '600px',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* Badge */}
                  <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl px-4 sm:px-6 py-2 sm:py-3 shadow-xl z-20">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon="star" />
                      <span className="text-body font-bold">Premium Access</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white via-purple-50/30 to-blue-50/30 relative" id="subscription-plans">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(77,162,255,0.05)_0%,transparent_50%),radial-gradient(circle_at_70%_30%,rgba(222,97,97,0.05)_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="tag" />
              <span>Pricing Plans</span>
            </div>
            
            <h2 className="text-h2 mb-4 md:mb-6">
              Choose Your <span className="text-gradient-blue-pink">Business Plan</span>
            </h2>
            
            <p className="text-body-lg text-gray mb-4 md:mb-8">
              Professional pricing designed for businesses. Scale your digital presence with confidence.
            </p>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {subscriptionPlans.map((plan, index) => (
              <div key={index} className="relative">
                <div className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative h-full ${
                  plan.popular ? 'ring-2 ring-blue-400/50' : ''
                }`}>
                  {plan.popular && (
                    <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-400 to-purple-500"></div>
                  )}
                  
                  {/* Plan Header */}
                  <div className="p-6 lg:p-8 text-center">
                    {plan.popular && (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-400 to-purple-500 text-white text-caption font-bold rounded-full mb-4">
                        <FontAwesomeIcon icon="star" />
                        MOST POPULAR
                      </div>
                    )}
                    
                    {plan.bonus && (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-400 to-pink-500 text-white text-caption font-bold rounded-full mb-4">
                        <FontAwesomeIcon icon="gift" />
                        {plan.bonus}
                      </div>
                    )}
                    
                    {plan.savings && (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 text-caption font-bold rounded-full mb-4">
                        <FontAwesomeIcon icon="tag" />
                        {plan.savings}
                      </div>
                    )}
                    
                    <h3 className="text-h4 text-dark mb-2">{plan.title}</h3>
                    <p className="text-body-sm text-gray mb-4">{plan.description}</p>
                    
                    <div className="flex items-baseline justify-center gap-1 mb-2">
                      <span className="text-h2 text-gradient-blue-pink">{plan.price}</span>
                      <span className="text-body text-gray">{plan.period}</span>
                    </div>
                    
                    {plan.yearlyPrice && (
                      <p className="text-body-sm text-green-600 font-medium mb-4">
                        Only {plan.yearlyPrice}
                      </p>
                    )}
                  </div>
                  
                  {/* Plan Features */}
                  <div className="px-6 lg:px-8 pb-6 lg:pb-8">
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r ${
                            index === 0 ? 'from-teal-400 to-teal-600' :
                            index === 1 ? 'from-blue-400 to-purple-600' :
                            'from-purple-400 to-pink-600'
                          } flex items-center justify-center mt-0.5`}>
                            <FontAwesomeIcon icon="check" className="text-white text-xs" />
                          </div>
                          <span className="text-body-sm text-gray">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button 
                      onClick={() => setShowComingSoonPopup(true)}
                      className={`btn w-full justify-center ${
                        plan.popular 
                          ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white hover:from-blue-500 hover:to-purple-600' 
                          : 'bg-gray-100 text-dark hover:bg-gradient-to-r hover:from-blue-400 hover:to-purple-500 hover:text-white'
                      }`}
                    >
                      {plan.buttonText}
                      <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Trust Badges */}
          <div className="mt-12 text-center" style={{ position: 'relative', zIndex: 100 }}>
            <div className="flex flex-wrap justify-center items-center gap-6 text-body-sm text-gray bg-white rounded-full px-8 py-4 mx-auto w-fit shadow-lg border border-gray-100">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
                <span className="font-medium">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="sync" className="text-blue-500" />
                <span className="font-medium">Cancel Anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="headset" className="text-purple-500" />
                <span className="font-medium">Priority Support</span>
              </div>
            </div>
          </div>
          
          {/* Team Dashboard CTA */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
              <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF818C]/20 rounded-full filter blur-3xl" />
              
              <div className="relative z-10">
                <div className="badge badge-white inline-flex mb-4 bg-white/20 backdrop-blur-sm border border-white/30">
                  <FontAwesomeIcon icon="clock" />
                  <span>Coming Soon</span>
                </div>
                
                <h3 className="text-h3 mb-4">
                  Team Dashboard Launching Soon!
                </h3>
                
                <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Get ready for team management features. Download AM CARDMATE now and be the first to know when our enterprise dashboard launches with volume discounts for 10+ members.
                </p>
                
                {/* App Store Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto mb-8" id="app-download">
                  <a 
                    href="https://apps.apple.com/pt/app/am-cardmate/id6747951046"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-black/20 backdrop-blur-sm px-6 py-4 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                  >
                    <FontAwesomeIcon icon={['fab', 'apple']} className="text-4xl" />
                    <div className="text-left">
                     <div className="text-caption text-white opacity-80">Download on the</div>
<div className="text-body text-white font-bold">App Store</div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.amcardmate.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-black/20 backdrop-blur-sm px-6 py-4 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                  >
                    <FontAwesomeIcon icon={['fab', 'google-play']} className="text-4xl" />
                    <div className="text-left">
                      <div className="text-caption text-white opacity-80">GET IT ON</div>
<div className="text-body text-white font-bold">Google Play</div>
                    </div>
                  </a>
                </div>
                
                {/* Waitlist Notice */}
                <div className="inline-flex items-center gap-2 text-body-sm text-white/80">
                  <FontAwesomeIcon icon="bell" className="text-yellow-400" />
                  <span>Join the waitlist for early access to team features</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* NFC Cards Section */}
      <div className="bg-gradient-to-b from-blue-50/30 to-white">
        <NFCCards />
      </div>
      
      {/* Testimonials */}
      <div className="bg-gradient-to-b from-white via-blue-50/20 to-white">
        <Testimonials 
          customTitle="Success Stories" 
          customDescription="See how businesses are transforming their networking with AM CARDMATE"
          customBadgeText="Business Reviews"
        />
      </div>
      
      {/* CTA */}
      <div className="bg-gradient-to-b from-white to-blue-50/30">
        <CTA 
          title="Ready to Elevate Your Professional Networking?"
          description="Join businesses worldwide using AM CARDMATE. Create up to 20 professional cards with comprehensive analytics and premium features."
          primaryButtonText="Get Started Today"
          primaryButtonIcon="rocket"
          primaryButtonHref="#subscription-plans"
          secondaryButtonText="View Analytics Demo"
          secondaryButtonIcon="chart-line"
          secondaryButtonHref="#analytics-dashboard"
          background="gradient"
        />
      </div>

      {/* Download Popup */}
      {showComingSoonPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowComingSoonPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="rocket" className="text-white text-2xl sm:text-3xl" />
              </div>
              
              <h3 className="text-h4 text-gray-900 mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray-600 mb-8">
                AM CARDMATE is now available on the App Store and Google Play!
              </p>
              
              <div className="grid grid-cols-1 gap-3 mb-6">
                <button 
                  onClick={() => window.open('https://apps.apple.com/pt/app/am-cardmate/id6747951046', '_blank')}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">Download on the</div>
                    <div className="text-body font-bold">App Store</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amcardmate.app', '_blank')}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">GET IT ON</div>
                    <div className="text-body font-bold">Google Play</div>
                  </div>
                </button>
              </div>
              
              <div className="space-y-3">
                <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-btn font-semibold hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => setShowComingSoonPopup(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessCard;